/**
 * PRD.330 — Pure report-query semantics.
 *
 * Validation, defaults, and the filter/sort state rules every report shares:
 *   - absent filter key = no filter; a values filter is NEVER empty;
 *   - different columns AND, values within one column OR (applied by the executor);
 *   - range bounds are inclusive integer minor units; min > max is rejected;
 *   - sort state is separate from filter state (clearing filters keeps sort,
 *     and sorting never counts as an active filter).
 *
 * TIMEZONE: report dates are LOCAL calendar dates. Day bounds use the
 * runtime's local timezone — the same convention as the Home dashboard's
 * `todayBounds()` and the payment dialogs' local-noon `paid_at` anchor.
 * PowerCare has no organization timezone setting; if one is introduced,
 * `localDayRangeToUtcBounds` is the single place to switch (PRD.330 §13.2).
 */
import { parseAmountInput } from '../money/index.js';
import type {
  ReportColumnFilter,
  ReportDatePresetId,
  ReportDateRange,
  ReportDefinition,
  ReportQuery,
  ReportQueryError,
  ReportRangeFilter,
  ReportSort,
  ReportUtcBounds,
  ReportValuesFilter,
} from './report-types.js';

// ─── Local calendar dates ───

const DATE_RE = /^(\d{4})-(\d{2})-(\d{2})$/;

function parseLocalDate(value: string): { y: number; m: number; d: number } | null {
  const match = DATE_RE.exec(value);
  if (!match) return null;
  const y = Number(match[1]);
  const m = Number(match[2]);
  const d = Number(match[3]);
  // Reject impossible dates (e.g. 2026-02-30) by round-tripping through Date.
  const probe = new Date(y, m - 1, d);
  if (probe.getFullYear() !== y || probe.getMonth() !== m - 1 || probe.getDate() !== d) return null;
  return { y, m, d };
}

export function isValidLocalDate(value: string): boolean {
  return typeof value === 'string' && parseLocalDate(value) !== null;
}

/** A Date's LOCAL calendar date as 'YYYY-MM-DD'. */
function formatLocalDate(date: Date): string {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

/** Today's LOCAL calendar date as 'YYYY-MM-DD'. */
export function localDateToday(now: Date = new Date()): string {
  return formatLocalDate(now);
}

// ─── Date presets (PRD.330 Phase 4) ───

export const REPORT_DATE_PRESET_LABELS: Readonly<Record<ReportDatePresetId, string>> = {
  today: 'Today',
  yesterday: 'Yesterday',
  last7Days: 'Last 7 days',
  thisMonth: 'This month',
  lastMonth: 'Last month',
  ytd: 'YTD',
};

/**
 * Resolve a preset into an inclusive LOCAL calendar-day range for `now`.
 * `new Date(y, m, d ± n)` normalizes month/year/leap rollover.
 *   today      today → today
 *   yesterday  today−1 → today−1
 *   last7Days  today−6 → today   (exactly 7 inclusive days)
 *   thisMonth  1st of this month → today (month-to-date)
 *   lastMonth  1st → last day of the previous month
 *   ytd        Jan 1 of this local calendar year → today
 */
export function resolveReportDatePreset(
  id: ReportDatePresetId,
  now: Date = new Date(),
): ReportDateRange {
  const y = now.getFullYear();
  const m = now.getMonth();
  const d = now.getDate();
  const day = (yy: number, mm: number, dd: number) => formatLocalDate(new Date(yy, mm, dd));
  switch (id) {
    case 'today':
      return { from: day(y, m, d), to: day(y, m, d) };
    case 'yesterday':
      return { from: day(y, m, d - 1), to: day(y, m, d - 1) };
    case 'last7Days':
      return { from: day(y, m, d - 6), to: day(y, m, d) };
    case 'thisMonth':
      return { from: day(y, m, 1), to: day(y, m, d) };
    case 'lastMonth':
      // Day 0 of this month = the last day of the previous month.
      return { from: day(y, m - 1, 1), to: day(y, m, 0) };
    case 'ytd':
      return { from: day(y, 0, 1), to: day(y, m, d) };
  }
}

/**
 * Convert an inclusive local-date range into the half-open UTC instant range
 * [start of `from` (local), start of the day after `to` (local)).
 * Throws on invalid dates — callers validate first.
 */
export function localDayRangeToUtcBounds(range: ReportDateRange): ReportUtcBounds {
  const from = parseLocalDate(range.from);
  const to = parseLocalDate(range.to);
  if (!from || !to) throw new Error(`Invalid report date range: ${range.from}..${range.to}`);
  const start = new Date(from.y, from.m - 1, from.d, 0, 0, 0, 0);
  const endExclusive = new Date(to.y, to.m - 1, to.d + 1, 0, 0, 0, 0);
  return { startUtc: start.toISOString(), endUtcExclusive: endExclusive.toISOString() };
}

// ─── Defaults / state transitions ───

/** The definition's default query: its default date preset resolved for `now`. */
export function defaultReportQuery<C extends string>(
  definition: ReportDefinition<C>,
  now: Date = new Date(),
): ReportQuery<C> {
  return {
    dateRange: resolveReportDatePreset(definition.defaultDatePreset, now),
    search: '',
    filters: {},
    sort: null,
    page: 1,
  };
}

/** The sort actually applied: the explicit sort, else the definition default. */
export function effectiveSort<C extends string>(
  definition: ReportDefinition<C>,
  query: ReportQuery<C>,
): ReportSort<C> {
  return query.sort ?? definition.defaultSort;
}

/** True only for global search or column filters — never for sort or date range. */
export function hasActiveFilters<C extends string>(query: ReportQuery<C>): boolean {
  if (query.search.trim() !== '') return true;
  return Object.values(query.filters).some((f) => f !== undefined);
}

/** "Clear filters": drop search + column filters; keep sort and date range. */
export function clearFilters<C extends string>(query: ReportQuery<C>): ReportQuery<C> {
  return { ...query, search: '', filters: {}, page: 1 };
}

/** Remove one column's applied filter (popover "Clear"). */
export function clearColumnFilter<C extends string>(
  query: ReportQuery<C>,
  columnId: C,
): ReportQuery<C> {
  const filters = { ...query.filters };
  delete filters[columnId];
  return { ...query, filters, page: 1 };
}

/** "Reset report": back to the definition default query. */
export function resetReport<C extends string>(
  definition: ReportDefinition<C>,
  now: Date = new Date(),
): ReportQuery<C> {
  return defaultReportQuery(definition, now);
}

export function reportPageCount(totalCount: number, pageSize: number): number {
  return Math.max(1, Math.ceil(Math.max(0, totalCount) / pageSize));
}

/** Clamp a page into [1, pageCount] — used when live updates shrink the result. */
export function clampReportPage(page: number, totalCount: number, pageSize: number): number {
  return Math.min(Math.max(1, Math.floor(page)), reportPageCount(totalCount, pageSize));
}

// ─── Derived aggregates (PRD.330 Phase 5) ───

/**
 * Add `ratio` aggregates (e.g. Average Payment = amountSum ÷ totalCount)
 * computed from the FULL-query summary values — never from page rows.
 * Rounded to a whole minor unit; 0 when the denominator is 0 or missing.
 */
export function deriveReportAggregates<C extends string>(
  definition: ReportDefinition<C>,
  summary: Readonly<Record<string, number>>,
): Record<string, number> {
  const out: Record<string, number> = { ...summary };
  for (const agg of definition.aggregates) {
    if (agg.kind !== 'ratio') continue;
    const numerator = Number(summary[agg.numeratorKey]) || 0;
    const denominator = Number(summary[agg.denominatorKey]) || 0;
    out[agg.key] = denominator > 0 ? Math.round(numerator / denominator) : 0;
  }
  return out;
}

// ─── Values (facet) selection ───

/**
 * Turn a popover selection into the value to APPLY.
 *   - empty selection → 'empty' (not appliable; the UI disables Apply);
 *   - Select All with no option-search text (every current option selected)
 *     → null = NO filter, so values that appear later are never excluded;
 *   - otherwise an explicit, de-duplicated values filter (stale selections kept).
 */
export function normalizeValuesSelection(input: {
  selected: ReadonlyArray<string>;
  allOptionValues: ReadonlyArray<string>;
  optionSearch: string;
}): ReportValuesFilter | null | 'empty' {
  const selected = [...new Set(input.selected)];
  if (selected.length === 0) return 'empty';
  const noOptionSearch = input.optionSearch.trim() === '';
  const selectedSet = new Set(selected);
  const coversAll =
    input.allOptionValues.length > 0 && input.allOptionValues.every((v) => selectedSet.has(v));
  if (noOptionSearch && coversAll) return null;
  return { kind: 'values', values: selected.sort() };
}

/** Option search inside a facet popover: filters visible OPTIONS, not report rows. */
export function filterFacetOptions<T extends { value: string; label: string }>(
  options: ReadonlyArray<T>,
  optionSearch: string,
): T[] {
  const term = optionSearch.trim().toLowerCase();
  if (!term) return [...options];
  return options.filter(
    (o) => o.label.toLowerCase().includes(term) || o.value.toLowerCase().includes(term),
  );
}

// ─── Amount (range) input ───

export type ReportAmountParse =
  | { kind: 'empty' }
  | { kind: 'ok'; cents: number }
  | { kind: 'error'; reason: 'invalid' | 'negative' };

/** Parse a major-unit (e.g. pesos) input to integer cents via the domain money helpers. */
export function parseReportAmountToCents(input: string): ReportAmountParse {
  const cleaned = input.replace(/,/g, '').trim();
  if (cleaned === '') return { kind: 'empty' };
  const num = Number(cleaned);
  if (!Number.isFinite(num)) return { kind: 'error', reason: 'invalid' };
  if (num < 0) return { kind: 'error', reason: 'negative' };
  const money = parseAmountInput(cleaned);
  if (!money) return { kind: 'error', reason: 'invalid' };
  return { kind: 'ok', cents: money.getAmount() };
}

export type ReportRangeDraftResult =
  | { kind: 'none' }
  | { kind: 'ok'; filter: ReportRangeFilter }
  | { kind: 'error'; field: 'min' | 'max' | 'range'; message: string };

/**
 * Validate a Min/Max draft (major units). Both empty → 'none' (same as Clear).
 * min > max → error; the applied query must not change.
 */
export function buildRangeFilterFromDraft(
  minInput: string,
  maxInput: string,
): ReportRangeDraftResult {
  const min = parseReportAmountToCents(minInput);
  const max = parseReportAmountToCents(maxInput);
  for (const [field, parsed] of [
    ['min', min],
    ['max', max],
  ] as const) {
    if (parsed.kind === 'error') {
      return {
        kind: 'error',
        field,
        message:
          parsed.reason === 'negative' ? 'Amount cannot be negative.' : 'Enter a valid amount.',
      };
    }
  }
  if (min.kind === 'empty' && max.kind === 'empty') return { kind: 'none' };
  const filter: ReportRangeFilter = { kind: 'range' };
  if (min.kind === 'ok') filter.min = min.cents;
  if (max.kind === 'ok') filter.max = max.cents;
  if (filter.min !== undefined && filter.max !== undefined && filter.min > filter.max) {
    return {
      kind: 'error',
      field: 'range',
      message: 'Minimum must be less than or equal to maximum.',
    };
  }
  return { kind: 'ok', filter };
}

// ─── Validation ───

function isNonNegativeInt(n: unknown): n is number {
  return typeof n === 'number' && Number.isSafeInteger(n) && n >= 0;
}

function validateFilter(
  columnId: string,
  filter: ReportColumnFilter,
  errors: ReportQueryError[],
): void {
  switch (filter.kind) {
    case 'text':
      if (typeof filter.value !== 'string' || filter.value.trim() === '') {
        errors.push({ code: 'empty_text_filter', columnId, message: 'Text filter is empty.' });
      }
      return;
    case 'values':
      if (
        !Array.isArray(filter.values) ||
        filter.values.length === 0 ||
        filter.values.some((v) => typeof v !== 'string')
      ) {
        errors.push({
          code: 'empty_values_filter',
          columnId,
          message: 'Select at least one value, or use Clear.',
        });
      }
      return;
    case 'range': {
      const { min, max } = filter;
      if (min === undefined && max === undefined) {
        errors.push({
          code: 'empty_range_filter',
          columnId,
          message: 'Enter a minimum or maximum.',
        });
        return;
      }
      if (
        (min !== undefined && !isNonNegativeInt(min)) ||
        (max !== undefined && !isNonNegativeInt(max))
      ) {
        errors.push({ code: 'invalid_range_bound', columnId, message: 'Enter a valid amount.' });
        return;
      }
      if (min !== undefined && max !== undefined && min > max) {
        errors.push({
          code: 'range_inverted',
          columnId,
          message: 'Minimum must be less than or equal to maximum.',
        });
      }
      return;
    }
    default:
      errors.push({ code: 'filter_kind_mismatch', columnId, message: 'Unsupported filter.' });
  }
}

/** Every rule the executor relies on. An empty array means the query is valid. */
export function validateReportQuery<C extends string>(
  definition: ReportDefinition<C>,
  query: ReportQuery<C>,
): ReportQueryError[] {
  const errors: ReportQueryError[] = [];
  const columns = new Map<string, ReportDefinition<C>['columns'][number]>(
    definition.columns.map((c) => [c.id, c]),
  );

  const { from, to } = query.dateRange ?? { from: '', to: '' };
  if (!isValidLocalDate(from) || !isValidLocalDate(to)) {
    errors.push({ code: 'invalid_date', message: 'Enter a valid date.' });
  } else if (from > to) {
    errors.push({
      code: 'date_range_inverted',
      message: 'Date From must be on or before Date To.',
    });
  }

  if (typeof query.search !== 'string') {
    errors.push({ code: 'invalid_search', message: 'Invalid search.' });
  }

  if (!Number.isSafeInteger(query.page) || query.page < 1) {
    errors.push({ code: 'invalid_page', message: 'Invalid page.' });
  }

  for (const [columnId, filter] of Object.entries(query.filters ?? {}) as Array<
    [string, ReportColumnFilter | undefined]
  >) {
    if (filter === undefined) continue;
    const column = columns.get(columnId);
    if (!column) {
      errors.push({ code: 'unknown_column', columnId, message: `Unknown column "${columnId}".` });
      continue;
    }
    if (column.filter === null) {
      errors.push({
        code: 'filter_not_supported',
        columnId,
        message: `${column.label} cannot be filtered.`,
      });
      continue;
    }
    if (filter.kind !== column.filter) {
      errors.push({
        code: 'filter_kind_mismatch',
        columnId,
        message: `Wrong filter type for ${column.label}.`,
      });
      continue;
    }
    validateFilter(columnId, filter, errors);
  }

  if (query.sort !== null && query.sort !== undefined) {
    const column = columns.get(query.sort.columnId);
    if (!column || !column.sortable) {
      errors.push({
        code: 'unsortable_column',
        columnId: query.sort.columnId,
        message: 'This column cannot be sorted.',
      });
    }
    if (query.sort.direction !== 'asc' && query.sort.direction !== 'desc') {
      errors.push({ code: 'invalid_sort_direction', message: 'Invalid sort direction.' });
    }
  }

  return errors;
}
